import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

interface Prefs { orderUpdates: boolean; promos: boolean; system: boolean }

const ITEMS: { key: keyof Prefs; icon: string; color: string }[] = [
  { key: 'orderUpdates', icon: 'receipt', color: '#00D632' },
  { key: 'promos', icon: 'tag', color: '#00D632' },
  { key: 'system', icon: 'cog', color: '#00D632' },
];

export default function NotificationPreferences() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<Prefs>('/me/notification-preferences')
      .then(r => setPrefs({ orderUpdates: r.data.orderUpdates, promos: r.data.promos, system: r.data.system }))
      .catch(() => setPrefs({ orderUpdates: true, promos: true, system: true }));
  }, []);

  const toggle = async (key: keyof Prefs) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try { await api.put('/me/notification-preferences', next); } catch { /* keep optimistic */ }
    finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('actions.back')} onPress={() => navigate(-1)} />
        <Icon name="bell" size={18} color={colors.accentDark} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{t('notificationPrefs.header')}</div>
        {saving && (
          <div style={{ marginLeft: 'auto' }}>
            <div style={{
              width: 18, height: 18, border: `2px solid ${colors.accentSoft}`,
              borderTopColor: colors.accentDark, borderRadius: '50%',
              animation: 'sls-spin 0.7s linear infinite',
            }} />
          </div>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {!prefs ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{
              width: 28, height: 28, border: `3px solid ${colors.accentSoft}`,
              borderTopColor: colors.accentDark, borderRadius: '50%',
              animation: 'sls-spin 0.7s linear infinite',
            }} />
          </div>
        ) : ITEMS.map(it => (
          <div
            key={it.key}
            style={{
              display: 'flex', alignItems: 'center',
              padding: 14, borderRadius: 14,
              backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              marginBottom: 10, minHeight: 72,
            }}
          >
            <div
              style={{
                width: 38, height: 38, borderRadius: 10,
                backgroundColor: colors.line,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: 12, flexShrink: 0,
              }}
            >
              <Icon name={it.icon} size={16} color={it.color} />
            </div>
            {/* minWidth:0 — without it the default `min-width:auto` stops this
                column shrinking, so a long description overflows under the
                switch instead of wrapping. */}
            <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{t(`notificationPrefs.items.${it.key}.label`)}</div>
              <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 1 }}>{t(`notificationPrefs.items.${it.key}.desc`)}</div>
            </div>
            <Switch checked={prefs[it.key]} onChange={() => toggle(it.key)} colors={colors} />
          </div>
        ))}
        <p style={{ margin: '18px 4px', color: colors.textSub, fontSize: 14, lineHeight: 1.5 }}>{t('notificationPrefs.importantNote')}</p>
      </div>
    </div>
  );
}

function Switch({ checked, onChange, colors }: { checked: boolean; onChange: () => void; colors: any }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        position: 'relative',
        width: 50, height: 30,
        borderRadius: 15,
        backgroundColor: checked ? colors.accentDark : colors.divider,
        border: 'none', cursor: 'pointer',
        transition: 'background-color 200ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3, left: checked ? 23 : 3,
          width: 24, height: 24,
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          transition: 'left 180ms ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        }}
      />
    </button>
  );
}
